/**
 * ADR-0235 — typed client for merchant trust verdicts + disputes.
 *
 *   GET  /v1/sites/:site_id/trust-verdicts?range=7d|30d&limit=500
 *   POST /v1/sites/:site_id/trust-disputes
 *
 * The verdict read is defensive: anything that is not a well-formed
 * `{ live: true, agents: [...] }` body is treated as "not live", so a
 * flag that is off, an API that is not deployed yet, or an error all
 * leave the dashboard exactly as it was.
 */

import {
  isDisputableLabel,
  type DisputableLabel,
  type TrustDisputeStatus,
  type VerdictLabel,
} from '@sill/types';
import type { MerchantAgentDisputeRef, MerchantAgentVerdict, TrustVerdictRange } from '@sill/types/api';

import { ApiError, type HttpClient } from './http-client.js';

export type TrustVerdictsState =
  | { readonly kind: 'off' }
  | { readonly kind: 'live'; readonly rule_version: string; readonly agents: readonly MerchantAgentVerdict[] };

const LABELS: ReadonlySet<string> = new Set(['verified', 'unverified', 'likely_impersonator']);
const STATUSES: ReadonlySet<string> = new Set(['open', 'upheld', 'overturned']);

function str(v: unknown): string | null {
  return typeof v === 'string' ? v : null;
}
function num(v: unknown): number {
  return typeof v === 'number' && Number.isFinite(v) && v >= 0 ? v : 0;
}

function parseDispute(raw: unknown): MerchantAgentDisputeRef | null {
  const o = typeof raw === 'object' && raw !== null ? (raw as Record<string, unknown>) : null;
  if (o === null) return null;
  const id = str(o['dispute_id']);
  const label = o['disputed_label'];
  const status = str(o['status']);
  if (id === null || !isDisputableLabel(label) || status === null || !STATUSES.has(status)) return null;
  return { dispute_id: id, disputed_label: label, status: status as TrustDisputeStatus, created_at: str(o['created_at']) ?? '' };
}

function parseAgent(raw: unknown): MerchantAgentVerdict | null {
  const o = typeof raw === 'object' && raw !== null ? (raw as Record<string, unknown>) : null;
  if (o === null) return null;
  const claim_key = str(o['claim_key']);
  const label = str(o['label']);
  if (claim_key === null || label === null || !LABELS.has(label)) return null;
  const counts = (typeof o['counts'] === 'object' && o['counts'] !== null ? o['counts'] : {}) as Record<string, unknown>;
  const reasons = Array.isArray(o['reasons']) ? (o['reasons'] as unknown[]) : [];
  const evidence = Array.isArray(o['evidence']) ? (o['evidence'] as unknown[]) : [];
  const suppressed = Array.isArray(o['suppressed']) ? (o['suppressed'] as unknown[]).filter(isDisputableLabel) : [];
  const disputes = Array.isArray(o['disputes']) ? (o['disputes'] as unknown[]).map(parseDispute).filter((d): d is MerchantAgentDisputeRef => d !== null) : [];
  return {
    claim_key,
    registry_agent_id: str(o['registry_agent_id']),
    label: label as VerdictLabel,
    suspicious: o['suspicious'] === true,
    impersonated: o['impersonated'] === true,
    counts: {
      verified: num(counts['verified']),
      unverified: num(counts['unverified']),
      likely_impersonator: num(counts['likely_impersonator']),
      suspicious: num(counts['suspicious']),
      total: num(counts['total']),
    },
    reasons: reasons.flatMap((r) => {
      const x = typeof r === 'object' && r !== null ? (r as Record<string, unknown>) : null;
      const code = x === null ? null : str(x['code']);
      if (x === null || code === null) return [];
      const family = str(x['family']);
      return [{
        code,
        family: family === 'verified' || family === 'likely_impersonator' || family === 'suspicious' ? family : 'unverified',
        count: num(x['count']),
      }];
    }),
    rule_version: str(o['rule_version']) ?? '',
    computed_at: str(o['computed_at']) ?? '',
    suppressed,
    operator: str(o['operator']),
    signers: Array.isArray(o['signers']) ? (o['signers'] as unknown[]).filter((s): s is string => typeof s === 'string') : [],
    evidence: evidence.flatMap((e) => {
      const x = typeof e === 'object' && e !== null ? (e as Record<string, unknown>) : null;
      const code = x === null ? null : str(x['code']);
      return x === null || code === null ? [] : [{ code, text: str(x['text']) ?? '', count: num(x['count']) }];
    }),
    disputes,
  };
}

/** Dashboard range → verdict window. */
export function verdictRangeFor(range: string): TrustVerdictRange {
  return range === '1d' || range === '7d' ? '7d' : '30d';
}

export async function getTrustVerdicts(http: HttpClient, site_id: string, range: TrustVerdictRange): Promise<TrustVerdictsState> {
  try {
    const res = await http.request<Record<string, unknown>>({
      method: 'GET',
      path: `/v1/sites/${encodeURIComponent(site_id)}/trust-verdicts?range=${range}&limit=500`,
    });
    if (res === undefined || res['live'] !== true || !Array.isArray(res['agents'])) return { kind: 'off' };
    const agents = (res['agents'] as unknown[]).map(parseAgent).filter((a): a is MerchantAgentVerdict => a !== null);
    return { kind: 'live', rule_version: str(res['rule_version']) ?? '', agents };
  } catch {
    return { kind: 'off' };
  }
}

export type CreateDisputeResult =
  | { readonly kind: 'ok'; readonly dispute: MerchantAgentDisputeRef }
  | { readonly kind: 'already_open' }
  | { readonly kind: 'rate_limited' }
  | { readonly kind: 'error'; readonly message: string };

export async function createTrustDispute(
  http: HttpClient,
  site_id: string,
  input: { readonly claim_key: string; readonly disputed_label: DisputableLabel; readonly note: string },
): Promise<CreateDisputeResult> {
  try {
    const note = input.note.trim();
    const res = await http.request<Record<string, unknown>>({
      method: 'POST',
      path: `/v1/sites/${encodeURIComponent(site_id)}/trust-disputes`,
      body: { claim_key: input.claim_key, disputed_label: input.disputed_label, ...(note.length > 0 ? { note } : {}) },
    });
    const d = parseDispute(res?.['dispute']);
    return d === null ? { kind: 'error', message: 'Unexpected response.' } : { kind: 'ok', dispute: d };
  } catch (err) {
    if (err instanceof ApiError) {
      if (err.kind === 'rate_limited') return { kind: 'rate_limited' };
      if (err.kind === 'conflict' && err.server_reason === 'dispute_open') return { kind: 'already_open' };
      return { kind: 'error', message: err.message };
    }
    return { kind: 'error', message: err instanceof Error ? err.message : 'Network error.' };
  }
}
